import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { FilmStrip, ChatCircleDots, Heart, Plus, X, ArrowLeft, User } from '@phosphor-icons/react';
import { motion } from 'framer-motion';
import Modal from '../components/Modal';
import { pressable } from '../lib/a11y';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
} from '../components/ui/alert-dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const INDUSTRIES = ['All', 'Tollywood', 'Bollywood', 'Kollywood', 'Hollywood'];

const Community = ({ user }) => {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [industry, setIndustry] = useState('All');
  const [showCreate, setShowCreate] = useState(false);
  const [newPost, setNewPost] = useState({ title: '', content: '', industry: 'Bollywood' });
  const [submitting, setSubmitting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const fetchPosts = useCallback(async () => {
    setLoading(true);
    try {
      const params = industry !== 'All' ? `?industry=${industry}` : '';
      const response = await axios.get(`${BACKEND_URL}/api/community/posts${params}`);
      setPosts(response.data);
    } catch (error) {
      toast.error('Failed to load posts');
    } finally {
      setLoading(false);
    }
  }, [industry]);

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  const authHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
  });

  const handleCreatePost = async () => {
    if (!user) {
      toast.error('Please login to post');
      navigate('/login');
      return;
    }
    if (!newPost.title.trim() || !newPost.content.trim()) {
      toast.error('Title and content are required');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/community/posts`, newPost, authHeaders());
      toast.success('Post created!');
      setShowCreate(false);
      setNewPost({ title: '', content: '', industry: 'Bollywood' });
      fetchPosts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create post');
    } finally {
      setSubmitting(false);
    }
  };

  const handleLike = async (postId, e) => {
    e.stopPropagation();
    if (!user) {
      toast.error('Please login to like');
      return;
    }
    try {
      const res = await axios.post(`${BACKEND_URL}/api/community/posts/${postId}/like`, {}, authHeaders());
      setPosts((prev) =>
        prev.map((p) =>
          p.id === postId
            ? {
                ...p,
                likes: res.data.liked
                  ? [...(p.likes || []), user.id]
                  : (p.likes || []).filter((id) => id !== user.id),
              }
            : p
        )
      );
    } catch (error) {
      toast.error('Failed to like post');
    }
  };

  const handleDelete = async (postId) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/community/posts/${postId}`, authHeaders());
      toast.success('Post deleted');
      setPosts((prev) => prev.filter((p) => p.id !== postId));
    } catch (error) {
      toast.error('Failed to delete');
    } finally {
      setDeleteTarget(null);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="noise-overlay" />

      <nav data-web-chrome="nav" className="relative z-10 glass-card sticky top-0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <button
              onClick={() => navigate('/')}
              data-testid="community-nav-home"
              className="flex items-center space-x-3"
            >
              <img src="/fanzingy-logo.jpeg" alt="FanZingy" className="h-10 w-10 rounded-full object-cover ring-1 ring-white/10" data-testid="brand-logo" />
            <span className="font-heading text-2xl font-bold tracking-tight">Fan<span className="text-gold">Zingy</span></span>
            </button>
            <div className="flex items-center space-x-3">
              <button
                data-testid="community-nav-marketplace"
                onClick={() => navigate('/marketplace')}
                className="btn-secondary hidden sm:inline-flex"
              >
                Marketplace
              </button>
              {user ? (
                <button
                  data-testid="community-create-btn"
                  onClick={() => setShowCreate(true)}
                  className="btn-primary flex items-center space-x-2"
                >
                  <Plus size={18} weight="bold" />
                  <span>New Post</span>
                </button>
              ) : (
                <button
                  data-testid="community-login-btn"
                  onClick={() => navigate('/login')}
                  className="btn-primary"
                >
                  Login to Post
                </button>
              )}
            </div>
          </div>
        </div>
      </nav>

      <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <button
          data-testid="community-back-btn"
          onClick={() => navigate(-1)}
          className="flex items-center space-x-2 text-slate-400 hover:text-gold mb-6 transition-colors"
        >
          <ArrowLeft size={20} />
          <span className="font-body text-sm">Back</span>
        </button>

        <div className="mb-10">
          <h1 className="font-heading text-5xl font-bold mb-2">
            FAN <span className="text-gold">COMMUNITY</span>
          </h1>
          <p className="font-body text-lg text-slate-400">
            Discuss films, share theories, and connect with fellow cinephiles
          </p>
        </div>

        {/* Industry Filter */}
        <div data-testid="community-industry-filter" className="flex gap-3 flex-wrap mb-8">
          {INDUSTRIES.map((ind) => (
            <button
              key={ind}
              data-testid={`community-filter-${ind.toLowerCase()}`}
              onClick={() => setIndustry(ind)}
              className={`px-5 py-2.5 rounded-sm font-body text-sm uppercase tracking-wide transition-all ${
                industry === ind
                  ? 'bg-gold text-black font-bold'
                  : 'bg-white/5 border border-white/10 text-slate-400 hover:bg-white/10'
              }`}
            >
              {ind}
            </button>
          ))}
        </div>

        {/* Posts list */}
        {loading ? (
          <div className="flex justify-center py-20">
            <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-gold" />
          </div>
        ) : posts.length === 0 ? (
          <div className="bg-card border border-white/5 rounded-lg p-12 text-center">
            <ChatCircleDots size={56} className="text-slate-400 mx-auto mb-4" />
            <p className="font-body text-lg text-slate-400 mb-4">
              No posts yet in {industry === 'All' ? 'the community' : industry}.
            </p>
            {user && (
              <button
                data-testid="community-empty-create-btn"
                onClick={() => setShowCreate(true)}
                className="btn-primary"
              >
                Start the conversation
              </button>
            )}
          </div>
        ) : (
          <div data-testid="community-posts-list" className="space-y-4">
            {posts.map((post, idx) => {
              const liked = user && (post.likes || []).includes(user.id);
              const canDelete = user && (user.role === 'admin' || user.id === post.author_id);
              return (
                <motion.div
                  key={post.id}
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.04 }}
                  className="bg-card border border-white/5 rounded-lg p-6 hover:border-gold/30 transition-colors cursor-pointer"
                  {...pressable(() => navigate(`/community/post/${post.id}`), `Open post: ${post.title}`)}
                  data-testid={`community-post-${post.id}`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 rounded-full bg-gold/10 border border-gold/30 flex items-center justify-center">
                        <User size={20} className="text-gold" weight="bold" />
                      </div>
                      <div>
                        <div className="font-body text-sm font-bold">{post.author_name}</div>
                        <div className="flex items-center gap-2 text-xs text-slate-400">
                          <span className="uppercase tracking-wide">{post.author_role}</span>
                          <span>•</span>
                          <span>{new Date(post.created_at).toLocaleDateString()}</span>
                        </div>
                      </div>
                    </div>
                    <span className="inline-block px-2.5 py-1 bg-gold/10 border border-gold/30 rounded-sm text-gold text-xs font-bold uppercase">
                      {post.industry}
                    </span>
                  </div>
                  <h3 className="font-heading text-xl font-bold mb-2">{post.title}</h3>
                  <p className="font-body text-slate-300 line-clamp-3 mb-4">{post.content}</p>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-5">
                      <button
                        data-testid={`community-like-${post.id}`}
                        aria-pressed={liked}
                        aria-label={`${liked ? 'Unlike' : 'Like'} post, ${(post.likes || []).length} likes`}
                        onClick={(e) => handleLike(post.id, e)}
                        className={`flex items-center gap-1.5 text-sm transition-colors ${
                          liked ? 'text-gold' : 'text-slate-400 hover:text-gold'
                        }`}
                      >
                        <Heart size={18} weight={liked ? 'fill' : 'regular'} />
                        <span className="font-mono tabular-nums">{(post.likes || []).length}</span>
                      </button>
                      <div className="flex items-center gap-1.5 text-slate-400 text-sm" aria-label={`${post.comment_count || 0} comments`}>
                        <ChatCircleDots size={18} aria-hidden="true" />
                        <span className="font-mono tabular-nums">{post.comment_count || 0}</span>
                      </div>
                    </div>
                    {canDelete && (
                      <button
                        data-testid={`community-delete-${post.id}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeleteTarget(post.id);
                        }}
                        className="text-xs text-slate-400 hover:text-red-400 uppercase tracking-wide"
                      >
                        Delete
                      </button>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>

      {/* Create Post Modal */}
      {showCreate && (
        <Modal onClose={() => setShowCreate(false)} title="Create post">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-card border border-white/10 rounded-lg w-full max-w-2xl p-8 relative"
            data-testid="community-create-modal"
          >
            <button aria-label="Close dialog"
              data-testid="community-modal-close"
              onClick={() => setShowCreate(false)}
              className="absolute top-4 right-4 text-slate-400 hover:text-white"
            >
              <X size={24} />
            </button>
            <h2 className="font-heading text-3xl font-bold mb-6">
              CREATE <span className="text-gold">POST</span>
            </h2>
            <div className="space-y-4">
              <div>
                <label className="block font-body text-xs uppercase tracking-wide text-slate-400 mb-2">
                  Industry
                </label>
                <select
                  data-testid="community-post-industry"
                  value={newPost.industry}
                  onChange={(e) => setNewPost({ ...newPost, industry: e.target.value })}
                  className="w-full bg-white/5 border border-white/10 rounded-sm px-4 py-3 text-white font-body focus:outline-none focus:border-gold"
                >
                  {INDUSTRIES.filter((i) => i !== 'All').map((i) => (
                    <option key={i} value={i}>{i}</option>
                  ))}
                  <option value="All">General (All)</option>
                </select>
              </div>
              <div>
                <label className="block font-body text-xs uppercase tracking-wide text-slate-400 mb-2">
                  Title
                </label>
                <input
                  data-testid="community-post-title"
                  type="text"
                  value={newPost.title}
                  onChange={(e) => setNewPost({ ...newPost, title: e.target.value })}
                  placeholder="What's the topic?"
                  className="w-full bg-white/5 border border-white/10 rounded-sm px-4 py-3 text-white font-body focus:outline-none focus:border-gold"
                />
              </div>
              <div>
                <label className="block font-body text-xs uppercase tracking-wide text-slate-400 mb-2">
                  Content
                </label>
                <textarea
                  data-testid="community-post-content"
                  rows={6}
                  value={newPost.content}
                  onChange={(e) => setNewPost({ ...newPost, content: e.target.value })}
                  placeholder="Share your thoughts..."
                  className="w-full bg-white/5 border border-white/10 rounded-sm px-4 py-3 text-white font-body focus:outline-none focus:border-gold resize-none"
                />
              </div>
              <button
                data-testid="community-post-submit"
                onClick={handleCreatePost}
                disabled={submitting}
                className="btn-primary w-full disabled:opacity-50"
              >
                {submitting ? 'Posting...' : 'Post to Community'}
              </button>
            </div>
          </motion.div>
        </Modal>
      )}

      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this post?</AlertDialogTitle>
            <AlertDialogDescription>This can't be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => handleDelete(deleteTarget)}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Community;
